import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarPlus, LayoutGrid, List, Plus, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { PageHeader } from "@/components/molecules/PageHeader";
import { useSettingsStore } from "@/store/settingsStore";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { FilterScope } from "@/components/molecules/FilterBar";
import { usePropertiesList, usePropertySelection } from "./propertiesHooks";
import { PropertiesFilters } from "./PropertiesFilters";
import { PropertiesTable } from "./PropertiesTable";
import { PropertiesTileGrid } from "./PropertiesTileGrid";
import { usePropertyImagePresence } from "./usePropertyImagePresence";
import { PropertyDetailDrawer } from "./PropertyDetailDrawer";
import type { PropertySummary } from "@/types/entities";
import {PropertyDrawer} from "@/modules/properties/PropertyDrawer";
import { AvailabilityCalendarDrawer } from "@/modules/availability/AvailabilityCalendarDrawer";
import {
  AvailabilityDrawer,
  type AvailabilityProperty,
} from "@/modules/availability/AvailabilityDrawer";
import type { RowSelection } from "@/components/organisms/DataTable";
import { useUiStore } from "@/store/uiStore";
import { useFilterUrlSync } from "@/hooks/useFilterUrlSync";
import { initialPageWithUrlSort, sortDefaultsFromPage, useSortUrlSync } from "@/hooks/useSortUrlSync";
import type { PageRequest, SortDirection } from "@/types/api";
import {
  CLEARED_PROPERTY_FILTER,
  PROPERTY_FILTER_PARAM_KEYS,
  parsePropertyFilterFromSearchParams,
  propertyFilterUrlSyncConfig,
} from "./propertyFilterParams";
import { useCreateShortcut } from "@/hooks/useCreateShortcut";
import { formatPrimaryShortcut } from "@/lib/keyboardShortcut";

type PropertyViewMode = "list" | "tile";

const PROPERTIES_INITIAL_PAGE: PageRequest = { page: 0, size: 30, sort: "atlasModifiedAt", direction: "desc" };
const PROPERTIES_SORT_DEFAULTS = sortDefaultsFromPage(PROPERTIES_INITIAL_PAGE);

export function PropertiesPage() {
  const [searchParams] = useSearchParams();
  const [initialFilter] = useState(() => parsePropertyFilterFromSearchParams(searchParams));
  const [initialPage] = useState(() => initialPageWithUrlSort(searchParams, PROPERTIES_INITIAL_PAGE));
  const { startPageLoading, stopPageLoading } = useUiStore();
  const list = usePropertiesList(initialFilter, initialPage);
  // Clear All Filters spans the scope picker too: it registers with the same FilterScope,
  // so it counts towards the badge and has to be reset by the same control.
  const clearScope = useSettingsStore((s) => s.clearScope);
  useFilterUrlSync(list.filter, list.setFilter, propertyFilterUrlSyncConfig);
  useSortUrlSync(list.pageRequest.sort, list.pageRequest.direction, list.setSort, PROPERTIES_SORT_DEFAULTS);
  const clearAll = useCallback(() => {
    clearScope("properties");
    list.setFilter(CLEARED_PROPERTY_FILTER);
  }, [clearScope, list]);
  const rows = useMemo(() => list.data?.content ?? [], [list.data?.content]);
  const {
    presenceByPropertyId,
    imageUrlsByPropertyId,
    fallbackLogoUrlByPropertyId,
    loading: imagePresenceLoading,
  } = usePropertyImagePresence(rows);
  const [selected, setSelected] = useState<PropertySummary | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // `selected` is the row as it was when clicked, so it never sees a reload.
  // Re-read the live row by id — only `GET /properties` carries `crmSyncStatus` / `syncAlto`.
  const selectedCrmSyncStatus = useMemo(() => {
    if (!selected || !rows) return undefined;
    const live = rows.find((r) => r.id === selected.id);
    return live?.crmSyncStatus ?? live?.syncAlto ?? selected.crmSyncStatus ?? selected.syncAlto;
  }, [rows, selected]);
  const [openCreate,setOpenCreate] = useState(false);
  const [viewMode, setViewMode] = useState<PropertyViewMode>(
    searchParams.get("view") === "tile" ? "tile" : "list",
  );
  const [availabilityProperty, setAvailabilityProperty] = useState<AvailabilityProperty | null>(null);

  // Multi-select for the bulk actions. Lives above the table so it survives paging.
  const selection = usePropertySelection();
  const [bulkAvailabilityOpen, setBulkAvailabilityOpen] = useState(false);

  const openCreateProperty = useCallback(() => {
    setSelected(null);
    setDrawerOpen(false);
    setOpenCreate(true);
  }, []);
  useCreateShortcut(openCreateProperty);

  const tableSelection = useMemo<RowSelection<PropertySummary>>(
    () => ({
      selectedKeys: selection.selectedKeys,
      onToggle: selection.toggle,
      onToggleAll: selection.toggleAll,
      selectAllLabel: "Select all properties on this page",
    }),
    [selection.selectedKeys, selection.toggle, selection.toggleAll],
  );

  const selectedAvailabilityProperties = useMemo<AvailabilityProperty[]>(
    () =>
      selection.selectedRows.map((row) => ({
        id: row.id,
        sourceCrm: row.sourceCrm,
        externalId: row.externalId,
        displayAddress: row.displayAddress,
        legalEntityId: null,
      })),
    [selection.selectedRows],
  );

  useEffect(() => {
    if (list.loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, startPageLoading, stopPageLoading]);

  const onAvailability = (row: PropertySummary) =>
    setAvailabilityProperty({
      id: row.id,
      sourceCrm: row.sourceCrm,
      externalId: row.externalId,
      displayAddress: row.displayAddress,
      legalEntityId: null,
    });

  const sortObj = useMemo(
    () =>
      list.pageRequest.sort && list.pageRequest.direction
        ? { field: list.pageRequest.sort, direction: list.pageRequest.direction }
        : undefined,
    [list.pageRequest.sort, list.pageRequest.direction],
  );

  const onSelect = (row: PropertySummary) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const onSortChange = (field: string, direction: SortDirection) => {
    if (field === "availabilityStatus" && rows.every((row) => !row.availabilityStatus?.trim())) return;
    list.setSort(field, direction);
  };

  const handlePropertySave = () => {
    setOpenCreate(false);
    list.reload();
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Properties"
        actions={
          <>
            <SavedFiltersButton
              pageName="properties"
              filterParamKeys={PROPERTY_FILTER_PARAM_KEYS}
              scopePage="properties"
            />
            <div className="flex items-center rounded-full border border-bridge-border/70 bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                aria-label="List view"
                className={`rounded-full p-2 transition-colors ${
                  viewMode === "list" ? "bg-[#009BF5] text-white" : "text-bridge-muted hover:text-[#009BF5]"
                }`}
              >
                <List size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("tile")}
                aria-label="Tile view"
                className={`rounded-full p-2 transition-colors ${
                  viewMode === "tile" ? "bg-[#009BF5] text-white" : "text-bridge-muted hover:text-[#009BF5]"
                }`}
              >
                <LayoutGrid size={16} />
              </button>
            </div>
            <RefreshButton onClick={list.reload} loading={list.loading} />
            <Tooltip content={`New listing (${formatPrimaryShortcut("k")})`} side="bottom">
              <Button
                variant="primary"
                size="sm"
                onClick={openCreateProperty}
              >
                <Plus size={14} /> New listing
              </Button>
            </Tooltip>
          </>
        }
      />
      {/* Spans the bar and the table so the column filters in the header count
          towards the same badge, and Clear Filters resets both at once. */}
      <FilterScope onClear={clearAll}>
      <div className="flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden flex flex-col">
        <PropertiesFilters filter={list.filter} onChange={list.setFilter} onClear={clearAll} />

        {/* Bulk bar. Only appears once something is ticked, and states the count it is
            about to act on — the same window is written to every selected property. */}
        {selection.selectedIds.length > 0 && (
          <div className="flex items-center gap-3 border-b border-bridge-border/60 bg-bridge-accent-tint px-6 py-2.5">
            <span className="text-sm font-medium text-slate-700">
              {selection.selectedIds.length} selected
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={selection.clear}
              className="inline-flex items-center gap-1 text-xs font-medium text-bridge-muted hover:text-bridge-fg"
            >
              <X size={12} /> Clear
            </button>
            <Button variant="primary" size="sm" onClick={() => setBulkAvailabilityOpen(true)}>
              <CalendarPlus size={14} /> Set availability
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-auto scrollbar-metal">
          {viewMode === "tile" ? (
            <PropertiesTileGrid
              rows={rows}
              imageUrlsByPropertyId={imageUrlsByPropertyId}
              fallbackLogoUrlByPropertyId={fallbackLogoUrlByPropertyId}
              onSelect={onSelect}
              onAvailability={onAvailability}
              returnView={viewMode}
              selection={{ selectedKeys: selection.selectedKeys, onToggle: selection.toggle }}
            />
          ) : (
            <PropertiesTable
              rows={rows}
              loading={list.loading}
              selectedKey={selected?.id ?? null}
              onSelect={onSelect}
              onAvailability={onAvailability}
              sort={sortObj}
              onSortChange={onSortChange}
              imagePresenceByPropertyId={presenceByPropertyId}
              imageUrlsByPropertyId={imageUrlsByPropertyId}
              fallbackLogoUrlByPropertyId={fallbackLogoUrlByPropertyId}
              imagePresenceLoading={imagePresenceLoading}
              returnView={viewMode}
              onResynced={list.reloadFirstPage}
              filter={list.filter}
              onFilterChange={list.setFilter}
              selection={tableSelection}
            />
          )}
        </div>
        <Pagination
          page={list.data?.page ?? list.pageRequest.page ?? 0}
          totalPages={list.data?.totalPages ?? 0}
          totalElements={list.data?.totalElements ?? 0}
          size={list.data?.size ?? list.pageRequest.size ?? 20}
          onPageChange={list.setPage}
        />
      </div>
      </FilterScope>

      <PropertyDetailDrawer
        open={drawerOpen}
        propertyId={selected?.id ?? null}
        crmSyncStatus={selectedCrmSyncStatus}
        onClose={() => setDrawerOpen(false)}
        onSaved={list.reload}
      />
        <PropertyDrawer open={openCreate} onClose={()=>setOpenCreate(false)} onSaved={handlePropertySave} />
      <AvailabilityCalendarDrawer
        open={availabilityProperty != null}
        property={availabilityProperty}
        onClose={() => setAvailabilityProperty(null)}
      />

      {/* Bulk manual availability — the same form as the single-property calendar, writing
          one availability entry per selected property. */}
      <AvailabilityDrawer
        open={bulkAvailabilityOpen && selectedAvailabilityProperties.length > 0}
        onClose={() => setBulkAvailabilityOpen(false)}
        properties={selectedAvailabilityProperties}
        onCreated={({ failed }) => {
          // A partial batch keeps its selection: the drawer stays open on the failures so
          // they can be retried without picking the properties again.
          if (failed === 0) selection.clear();
          list.reload();
        }}
      />
    </div>
  );
}
